import { useEffect, useState } from "react";
import { Clock3, FolderOpen, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CreateProjectDialog } from "@/components/projects/create-project-dialog";
import { DeleteProjectDialog } from "@/components/projects/delete-project-dialog";
import { ProjectAccessSelectionGate } from "@/components/projects/project-access-selection-gate";
import { ProjectCard } from "@/components/projects/project-card";
import { ProjectHomeEmptyState } from "@/components/projects/project-home-empty-state";
import { ProjectSettingsDialog } from "@/components/projects/project-settings-dialog";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import type { ProjectCardView } from "@/features/projects";
import { useProjectShellStore } from "@/stores/projectShellStore";
import { Badge } from "@/components/ui/badge";

export function AppHomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [settingsTarget, setSettingsTarget] = useState<ProjectCardView | null>(null);
  const activeProjects = useProjectShellStore((state) => state.activeProjects);
  const archivedProjects = useProjectShellStore((state) => state.archivedProjects);
  const archiveProject = useProjectShellStore((state) => state.archiveProject);
  const capacity = useProjectShellStore((state) => state.capacity);

  const clearNotice = useProjectShellStore((state) => state.clearNotice);
  const clearUpgradePrompt = useProjectShellStore((state) => state.clearUpgradePrompt);
  const hydratePortfolio = useProjectShellStore((state) => state.hydratePortfolio);
  const notice = useProjectShellStore((state) => state.notice);
  const openDeleteDialog = useProjectShellStore((state) => state.openDeleteDialog);
  const portfolioStatus = useProjectShellStore((state) => state.portfolioStatus);
  const projectAccessSelection = useProjectShellStore((state) => state.projectAccessSelection);
  const projectAccessSelectionStatus = useProjectShellStore((state) => state.projectAccessSelectionStatus);
  const projectAccessSelectionSubmitting = useProjectShellStore(
    (state) => state.projectAccessSelectionSubmitting,
  );
  const submitProjectAccessSelection = useProjectShellStore(
    (state) => state.submitProjectAccessSelection,
  );
  const upgradePrompt = useProjectShellStore((state) => state.upgradePrompt);
  const requiresProjectSelection = projectAccessSelection?.required === true;

  useEffect(() => {
    void hydratePortfolio();
  }, [hydratePortfolio]);

  return (
    <div className="project-shell-page">
      <section className="project-shell-hero project-shell-soft-grid overflow-hidden px-6 py-7 sm:px-8">
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_21rem]">
          <div className="space-y-6">
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-clay">
                {t("projects.portfolio.eyebrow")}
              </p>
              <div className="space-y-4">
                <h1 className="max-w-3xl font-display text-4xl leading-none text-ink sm:text-5xl lg:text-6xl">
                  {t("projects.portfolio.title")}
                </h1>
                <p className="max-w-2xl text-base leading-7 text-ink/70">
                  {t("projects.portfolio.subtitle")}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {requiresProjectSelection ? (
                <p className="text-sm text-amber-900/85">
                  {t("projects.accessSelection.heroLockNotice")}
                </p>
              ) : (
                <>
                  <Button
                    onClick={() => setCreateOpen(true)}
                    type="button"
                    className="gap-2"
                  >
                    <Sparkles className="h-4 w-4" />
                    {t("projects.portfolio.createAction")}
                  </Button>
                  <Button
                    onClick={() => navigate("/app/story-import")}
                    type="button"
                    variant="outline"
                  >
                    {t("storyImport.launchAction")}
                  </Button>
                </>
              )}
            </div>
          </div>

          <div className="project-shell-panel project-shell-mesh px-5 py-5">
            <div className="flex h-full flex-col justify-between gap-6">
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-ink/55">
                  {t("projects.dashboard.title")}
                </p>
                <p className="text-sm leading-6 text-ink/70">
                  {capacity
                    ? t("projects.dashboard.capacityUsage", {
                        limit: capacity.projectsLimit,
                        used: capacity.projectsUsed,
                      })
                    : t("projects.portfolio.emptyBody")}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
                <div className="project-shell-stat-card">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink/45">
                    {t("projects.portfolio.activeSection")}
                  </p>
                  <p className="mt-2 font-display text-4xl text-ink">{activeProjects.length}</p>
                </div>
                <div className="project-shell-stat-card">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-ink/45">
                    {t("projects.portfolio.archivedSection")}
                  </p>
                  <p className="mt-2 font-display text-4xl text-ink">{archivedProjects.length}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      {notice ? (
        <Card className="project-shell-panel">
          <CardContent className="flex items-start justify-between gap-4 p-5">
            <p className="text-sm text-foreground">{t(notice.message)}</p>
            <button
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              onClick={clearNotice}
              type="button"
            >
              {t("projects.actions.dismiss")}
            </button>
          </CardContent>
        </Card>
      ) : null}

      {upgradePrompt ? (
        <Card className="project-shell-panel">
          <CardContent className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-2">
              <p className="text-lg font-semibold text-foreground">
                {t("projects.portfolio.upgradeTitle")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("projects.portfolio.upgradeBody", {
                  allowed: upgradePrompt.allowedCount,
                  tier: upgradePrompt.tier,
                })}
              </p>
            </div>
            <Button onClick={clearUpgradePrompt} type="button" variant="outline">
              {t("projects.actions.dismiss")}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {portfolioStatus === "loading" && !activeProjects.length && !archivedProjects.length ? (
        <Card className="project-shell-panel">
          <CardContent className="flex items-center gap-3 p-6 text-muted-foreground">
            <Clock3 className="h-5 w-5" />
            {t("projects.portfolio.loading")}
          </CardContent>
        </Card>
      ) : null}

      {requiresProjectSelection && projectAccessSelection ? (
        <ProjectAccessSelectionGate
          busy={projectAccessSelectionSubmitting}
          selection={projectAccessSelection}
          onSubmit={(projectIds) => submitProjectAccessSelection(projectIds)}
        />
      ) : null}

      {!requiresProjectSelection
      && !activeProjects.length
      && !archivedProjects.length
      && portfolioStatus !== "loading"
      && projectAccessSelectionStatus !== "loading" ? (
        <ProjectHomeEmptyState onCreateProject={() => setCreateOpen(true)} />
      ) : (
        !requiresProjectSelection ? (
          <div className="space-y-8">
          <section className="space-y-4">
            <div className="flex items-end justify-between gap-4">
              <div className="flex items-center gap-3">
                <FolderOpen className="h-5 w-5 text-clay" />
                <h2 className="text-3xl text-foreground">{t("projects.portfolio.activeSection")}</h2>
              </div>
              {capacity ? (
                <Badge variant="outline" className="text-[11px] tracking-[0.2em]">
                  {t("projects.dashboard.capacityUsage", {
                    limit: capacity.projectsLimit,
                    used: capacity.projectsUsed,
                  })}
                </Badge>
              ) : null}
            </div>
            {activeProjects.length ? (
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {activeProjects.map((project) => (
                  <ProjectCard
                    key={project.projectId}
                    onArchive={() => void archiveProject(project.projectId)}
                    onDelete={() => openDeleteDialog(project)}
                    onOpen={() => navigate(`/projects/${project.projectId}`)}
                    onSettings={() => setSettingsTarget(project)}
                    project={project}
                  />
                ))}
              </div>
            ) : (
              <Card className="project-shell-panel">
                <CardContent className="p-6 text-sm text-muted-foreground">
                  {t("projects.portfolio.noActive")}
                </CardContent>
              </Card>
            )}
          </section>

          {archivedProjects.length ? (
            <section className="space-y-4">
              <div className="flex items-center gap-3">
                <Clock3 className="h-5 w-5 text-clay" />
                <h2 className="text-3xl text-foreground">
                  {t("projects.portfolio.archivedSection")}
                </h2>
              </div>
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {archivedProjects.map((project) => (
                  <ProjectCard
                    key={project.projectId}
                    onArchive={() => undefined}
                    onDelete={() => openDeleteDialog(project)}
                    onOpen={() => navigate(`/projects/${project.projectId}`)}
                    onSettings={() => setSettingsTarget(project)}
                    project={project}
                  />
                ))}
              </div>
            </section>
          ) : null}
          </div>
        ) : null
      )}

      <CreateProjectDialog onOpenChange={setCreateOpen} open={createOpen} />
      <ProjectSettingsDialog
        onOpenChange={(open) => {
          if (!open) {
            setSettingsTarget(null);
          }
        }}
        open={Boolean(settingsTarget)}
        project={settingsTarget}
      />
      <DeleteProjectDialog />
    </div>
  );
}
